<template>
  <div class="homepage__layout-wrap" :class="homepageDynamicClass">
    <main class="homepage-wrap">
      <homepage-top-article
        v-if="isDataValid.TopArticles"
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.TOP_ARTICLE
          )
        "
        :article="homepageData.Fm.Articles.TopArticles[0]"
        :last-modified="lastModifiedByRequest.FmArticles"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.TOP_ARTICLE
          )
        "
      />

      <a-buzz-sprout-player class="homepage__buzz-widget" />

      <homepage-latest-news
        v-if="isDataValid.LatestNews"
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.LATEST_NEWS
          )
        "
        :articles="homepageData.Fm.Articles.LatestNewsArticles"
        :last-modified="lastModifiedByRequest.FmArticles"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.LATEST_NEWS
          )
        "
      />

      <homepage-combined
        :data-ref="$options.consts.REFS.HOMEPAGE_COMBINED_SECTION"
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.COMBINED
          )
        "
        :education-center-articles="homepageData.Fm.Articles.EducationArticles"
        :thought-leadership-articles="
          homepageData.Fm.Articles.ThoughtLeadershipArticles
        "
        :directory-categories="homepageData.FmDir.Categories"
        :directory-companies="homepageData.FmDir.Companies"
        :directory-news="homepageData.FmDir.News"
        :most-viewed-articles="homepageData.Fm.Articles.MostViewedArticles"
        :is-data-valid="isDataValid"
        :last-modified-by-request="lastModifiedByRequest"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.COMBINED
          )
        "
      />

      <homepage-videos
        v-if="isDataValid.Videos"
        :data-ref="$options.consts.REFS.HOMEPAGE_VIDEOS_SECTION"
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.VIDEOS
          )
        "
        :videos="homepageData.Fm.Videos"
        :last-modified="lastModifiedByRequest.Videos"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.VIDEOS
          )
        "
      />

      <homepage-events
        v-if="isDataValid.Events"
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.EVENTS
          )
        "
        :events="homepageData.Fm.Events"
        :last-modified="lastModifiedByRequest.Events"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.EVENTS
          )
        "
      />

      <!-- Temporarily hide the Investinglive section until BE fix the endpoint -->

      <!-- <homepage-investinglive
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.INVESTINGLIVE
          )
        "
        :latest-news="homepageData.Il.Articles.LatestNews"
        :technical-analysis-news="homepageData.Il.Articles.TechnicalAnalysis"
        :is-data-valid="isDataValid"
        :last-modified-by-request="lastModifiedByRequest"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.INVESTINGLIVE
          )
        "
      /> -->

      <a-visibility show :on="[$breakpoint.mobile]">
        <a-interstitial-banner
          :banner-settings="BANNER_SETTINGS.HOMEPAGE.INTERSTITIAL"
          class="homepage__interstitial-banner"
        />
      </a-visibility>
    </main>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import mixins from '~/utils/mixins'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import { META_PARAM } from 'enums/seo'
import { ASSISTANT_BUTTON_ID } from 'shared/AAssistantButton/enums'
import { REFS } from 'enums/external-refs'
import { throwPageNotFoundError } from '@/plugins/error'
import {
  generateArticlePathBySlugs,
  promiseAllWithFallback
} from '@/plugins/helper'
import { QUERY_PARAM } from 'enums/query-param'

import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { SRCSET_LOCATION, getImagePreloadLink } from '@/utils/enums/images'

const hydrationOptions = { observerOptions: { rootMargin: '100px' } }

function isNonEmptyArrayWithField(value, field) {
  if (!value) return false
  if (!Array.isArray(value)) return false

  return value.length && !!value.some(item => item && item[field])
}

const COMPANY_SLOT_COUNT = 10

/**
 * This function generates array of 10 slots with directory companies.
 * Some slots could be empty and in such a case will be rendered as empty slot
 * @param directoryCompanies
 */
function generateCompanySlots(directoryCompanies) {
  const companySlots = new Array(COMPANY_SLOT_COUNT).fill(null)
  directoryCompanies.forEach((company, index) => {
    const targetIndex = company.Order == null ? index : company.Order
    companySlots[targetIndex] = company
  })
  return companySlots
}

async function getHomepageData({ ctx, store }) {
  const { dispatch } = store

  const promiseSettings = [
    {
      promise: dispatch('articles/requestHomepageFmArticles'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Fm Articles',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestDirectoryCategories'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Directory Categories',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestDirectoryCompanies'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Directory Companies',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestDirectoryNews'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Directory News',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestHomepageVideos'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Videos section',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestHomepageEvents'),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Homepage - Events section',
      fallbackData: {}
    },
    /**
     * We should request form settings here to avoid DOM mismatch when the
     * Newsletter form is rendered on the Homepage.
     */
    {
      promise: dispatch('leads/requestFormsSettings'),
      required: true,
      errorMessage: 'Error has occurred while trying to get form settings',
      fallbackData: {}
    }
  ]

  const promiseAllResult = await promiseAllWithFallback(promiseSettings, ctx)

  const [
    {
      data: {
        TopArticles,
        LatestNewsArticles,
        EducationArticles,
        MostViewedArticles,
        ThoughtLeadershipArticles,
        lastModified: fmArticlesLastModified
      }
    },
    {
      data: {
        Categories: DirectoryCategories,
        lastModified: directoryCategoriesLastModified
      }
    },
    {
      data: {
        Companies: DirectoryCompanies,
        lastModified: directoryCompaniesLastModified
      }
    },
    {
      data: { News: DirectoryNews, lastModified: directoryNewsLastModified }
    },
    {
      data: { Videos, lastModified: videosLastModified }
    },
    {
      data: { Events, lastModified: eventsLastModified }
    }
  ] = promiseAllResult

  return {
    homepageData: {
      Fm: {
        Articles: {
          TopArticles,
          LatestNewsArticles: LatestNewsArticles.filter(category =>
            isNonEmptyArrayWithField(category.Articles, 'slug')
          ),
          EducationArticles,
          ThoughtLeadershipArticles,
          MostViewedArticles
        },
        Videos,
        Events
      },
      FmDir: {
        Categories: DirectoryCategories,
        Companies: generateCompanySlots(DirectoryCompanies),
        News: DirectoryNews
      },
      Il: {
        Articles: {
          LatestNews: [],
          TechnicalAnalysis: []
        }
      }
    },
    lastModifiedByRequest: {
      FmArticles: fmArticlesLastModified,
      DirectoryCompanies: directoryCompaniesLastModified,
      DirectoryCategories: directoryCategoriesLastModified,
      DirectoryNews: directoryNewsLastModified,
      Videos: videosLastModified,
      Events: eventsLastModified
    }
  }
}

const HYDRATED_SECTION_NAME = {
  TOP_ARTICLE: 'TOP_ARTICLE',
  LATEST_NEWS: 'LATEST_NEWS',
  COMBINED: 'COMBINED',
  VIDEOS: 'VIDEOS',
  EVENTS: 'EVENTS',
  INVESTINGLIVE: 'INVESTINGLIVE'
}

export default {
  name: 'Homepage',
  mixins: [
    mixins.urlFormatters,
    mixins.setPageLoaded,
    mixins.initialScroll,
    mixins.cmp,
    mixins.lazyHydratedComponentState,
    mixins.delayedContentRefresh
  ],
  components: {
    ABuzzSproutPlayer: () => import('@/components/_shared/ABuzzSproutPlayer'),
    AInterstitialBanner: hydrateWhenVisible(
      () => import('@/components/_layout/AInterstitialBanner'),
      { ...hydrationOptions, props: ['banner-settings'] }
    ),
    HomepageTopArticle: hydrateWhenVisible(
      () => import('@/components/_homepage/Sections/HomepageTopArticle'),
      { ...hydrationOptions, props: ['article', 'last-modified'] }
    ),
    HomepageLatestNews: hydrateWhenVisible(
      () => import('@/components/_homepage/Sections/HomepageLatestNews'),
      { ...hydrationOptions, props: ['articles', 'last-modified'] }
    ),
    HomepageCombined: hydrateWhenVisible(
      () => import('@/components/_homepage/Sections/HomepageCombined'),
      {
        ...hydrationOptions,
        props: [
          'education-center-articles',
          'thought-leadership-articles',
          'directory-categories',
          'directory-companies',
          'directory-news',
          'most-viewed-articles',
          'is-data-valid',
          'last-modified-by-request'
        ]
      }
    ),
    HomepageVideos: hydrateWhenVisible(
      () => import('@/components/_homepage/Sections/HomepageVideos'),
      { ...hydrationOptions, props: ['videos', 'last-modified'] }
    ),
    HomepageEvents: hydrateWhenVisible(
      () => import('@/components/_homepage/Sections/HomepageEvents'),
      { ...hydrationOptions, props: ['events', 'last-modified'] }
    )
    // HomepageInvestinglive: hydrateWhenVisible(
    //   () => import('@/components/_homepage/Sections/HomepageInvestinglive'),
    //   {
    //     ...hydrationOptions,
    //     props: [
    //       'latest-news',
    //       'technical-analysis-news',
    //       'is-data-valid',
    //       'last-modified-by-request'
    //     ]
    //   }
    // )
  },
  head() {
    const {
      Fm: {
        Articles: {
          LatestNewsArticles = [],
          TopArticles = [],
          ThoughtLeadershipArticles = [],
          EducationArticles = [],
          MostViewedArticles = []
        },
        Videos = {},
        Events
      },
      Il: {
        Articles: { LatestNews: IlLatestNews }
      }
    } = this.homepageData

    const lcpImagePreloadLink = getImagePreloadLink({
      url: TopArticles[0].featuredImage?.url,
      location: SRCSET_LOCATION.HOMEPAGE_TOP_ARTICLE
    })

    /**
     * Temporary hardcode the title due to the bug FMP-17163
     */
    return this.$generateMetaTags({
      titleParam: META_PARAM.HOMEPAGE,
      descriptionParam: META_PARAM.HOMEPAGE,
      schemes: [
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        ...this.$helper.generateVideoSchemasFromResponse([
          TopArticles[0]?.featuredVideo,
          ...LatestNewsArticles.map(
            category => category.Articles?.[0]?.featuredVideo
          ),
          ...EducationArticles.map(article => article?.featuredVideo),
          ...ThoughtLeadershipArticles.map(article => article?.featuredVideo),
          ...MostViewedArticles.map(article => article?.featuredVideo),
          Events?.VideoSettings?.Video,
          ...Object.values(Videos).map(videoSettings => videoSettings.Video),
          ...(IlLatestNews || []).map(article => article?.FeaturedVideo)
        ])
      ],
      additionalLinks: [
        ...(lcpImagePreloadLink ? [lcpImagePreloadLink] : []),
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'Finance Magnates | Financial and business news',
          href: `${this.$env.DOMAIN_URL}/feed`
        }
      ],
      source: 'seo/homepageSeo',
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { fullPath } = ctx.route
    const dispatch = ctx.store.dispatch

    const {
      query: { p: legacyArticleId }
    } = ctx.route

    /**
     * Handle shortlinks
     */
    if (legacyArticleId) {
      const payload = { wp_id: legacyArticleId }
      try {
        const { Slug, CategorySlug, SubCategorySlug } = await dispatch(
          'articles/requestMigratedArticleSlugsByShortLink',
          payload
        )

        const articleUrl = generateArticlePathBySlugs({
          articleSlug: Slug,
          categorySlug: CategorySlug,
          subcategorySlug: SubCategorySlug
        })

        redirectHandler({ ctx, to: articleUrl })
      } catch (err) {
        return throwPageNotFoundError(ctx, 'HomePage with shortlink')
      } finally {
        await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
      }
    }

    try {
      const { homepageData, lastModifiedByRequest } = await getHomepageData({
        ctx,
        store: ctx.store
      })

      return {
        homepageData,
        lastModifiedByRequest
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, { showErrorPage: true })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  consts: {
    ASSISTANT_BUTTON_ID,
    REFS,
    HYDRATED_SECTION_NAME
  },
  data() {
    return {
      canonicalUrl: this.$env.DOMAIN_URL,
      BANNER_SETTINGS,
      homepageData: {
        Fm: {
          Articles: {
            TopArticles: [],
            LatestNewsArticles: [],
            EducationArticles: [],
            ThoughtLeadershipArticles: [],
            MostViewedArticles: []
          },
          Videos: [],
          Events: {}
        },
        FmDir: {
          Categories: [],
          Companies: [],
          News: []
        },
        Il: {
          Articles: {
            LatestNews: [],
            TechnicalAnalysis: []
          }
        }
      },
      lastModifiedByRequest: {
        FmArticles: null,
        DirectoryCompanies: null,
        DirectoryCategories: null,
        DirectoryNews: null,
        Videos: null,
        Events: null
      }
    }
  },
  computed: {
    ...mapGetters({
      isPreviewMode: 'isPreviewMode'
    }),
    homepageDynamicClass() {
      const {
        [QUERY_PARAM.HIGHLIGHT_CUSTOM_FONT]: highlight
      } = this.$route.query

      return {
        'highlight-custom-fonts': !!highlight
      }
    },
    isDataValid() {
      return {
        TopArticles: this.isTopArticlesDataValid,
        LatestNews: this.isLatestNewsDataValid,
        EducationArticles: this.isEducationArticlesDataValid,
        ThoughtLeadershipArticles: this.isThoughtLeadershipArticlesDataValid,
        MostViewedArticles: this.isMostViewedArticlesDataValid,
        DirectoryCompanies: this.isDirectoryCompaniesDataValid,
        DirectoryCategories: this.isDirectoryCategoriesDataValid,
        DirectoryNews: this.isDirectoryNewsDataValid,
        Videos: this.isVideosDataValid,
        Events: this.isEventDataValid,
        InvestingliveLatestNews: this.isInvestingliveLatestNewsDataValid,
        InvestingliveTechnicalAnalysisNews: this
          .isInvestingliveTechnicalAnalysisNewsDataValid
      }
    },
    isTopArticlesDataValid() {
      try {
        const { TopArticles } = this.homepageData.Fm.Articles
        return isNonEmptyArrayWithField(TopArticles, 'slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isLatestNewsDataValid() {
      try {
        const { LatestNewsArticles } = this.homepageData.Fm.Articles
        return (
          LatestNewsArticles.length &&
          LatestNewsArticles.some(category =>
            isNonEmptyArrayWithField(category.Articles, 'slug')
          )
        )
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isEducationArticlesDataValid() {
      try {
        const { EducationArticles } = this.homepageData.Fm.Articles
        return isNonEmptyArrayWithField(EducationArticles, 'slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isThoughtLeadershipArticlesDataValid() {
      try {
        const { ThoughtLeadershipArticles } = this.homepageData.Fm.Articles
        return isNonEmptyArrayWithField(ThoughtLeadershipArticles, 'slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isMostViewedArticlesDataValid() {
      try {
        const { MostViewedArticles } = this.homepageData.Fm.Articles
        return isNonEmptyArrayWithField(MostViewedArticles, 'slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isDirectoryCompaniesDataValid() {
      try {
        const { Companies } = this.homepageData.FmDir
        return isNonEmptyArrayWithField(Companies, 'Slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isDirectoryCategoriesDataValid() {
      try {
        const { Categories } = this.homepageData.FmDir
        return isNonEmptyArrayWithField(Categories, 'Slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isDirectoryNewsDataValid() {
      try {
        const { News } = this.homepageData.FmDir
        return isNonEmptyArrayWithField(News, 'Slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isVideosDataValid() {
      try {
        const { Videos } = this.homepageData.Fm
        return isNonEmptyArrayWithField(Videos, 'Video')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isEventDataValid() {
      try {
        const eventData = this.homepageData.Fm.Events
        const keys = [
          'Events',
          'MainEvent',
          'Newsletter',
          'Testimonials',
          'VideoSettings'
        ]

        if (!keys.every(key => eventData[key])) return false
        if (
          !eventData.Events.EventsList ||
          !isNonEmptyArrayWithField(eventData.Events.EventsList, 'Title')
        ) {
          return false
        }

        return isNonEmptyArrayWithField(eventData.Testimonials.Items, 'Title')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isInvestingliveLatestNewsDataValid() {
      try {
        const { LatestNews } = this.homepageData.Il.Articles
        return isNonEmptyArrayWithField(LatestNews, 'Slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    },
    isInvestingliveTechnicalAnalysisNewsDataValid() {
      try {
        const { TechnicalAnalysis } = this.homepageData.Il.Articles
        return isNonEmptyArrayWithField(TechnicalAnalysis, 'Slug')
      } catch (err) {
        this.$errorHandler(err, this)
        return false
      }
    }
  },
  mounted() {
    this.$_delayedContentRefresh_run({
      getOldDataFn: () => this.homepageData,
      requestNewDataFn: async () => {
        const { homepageData } = await getHomepageData({
          ctx: this,
          store: this.$store
        })

        return homepageData
      },
      dataComparisonKeysToOmitDeep: ['ModifiedOn'],
      dataChangedCallbackFn: homepageData => {
        this.homepageData = homepageData
        this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
          Object.values(HYDRATED_SECTION_NAME)
        )
      }
    })
  }
}
</script>

<style lang="scss" scoped>
$latest-section-width: 710px;
$content-width-desktop-lg: 990px;

.homepage-wrap {
  .homepage__buzz-widget {
    margin-top: 40px;

    @include mobile {
      margin-top: 20px;
    }
  }

  .homepage__interstitial-banner {
    position: fixed;
  }

  & > * {
    position: relative;
  }

  /deep/ & > section {
    margin-top: 40px;

    @include mobile {
      margin-top: 20px;
    }

    &:first-child {
      margin-top: 0;
    }
  }

  & > *:last-child {
    &::after {
      display: none;
    }
  }

  /deep/ .input__wrapper,
  /deep/ .a-button__general-style {
    border-radius: 5px;
  }
}
</style>
